import { useEffect, useMemo, useState } from "react";
import { ContainerTimeConfig } from "@/types/v2/Metrics";
import { DateRange } from "@/components/Metrics/DateRangeSelector";

export type TimeEvent = "enters" | "exits";
export type ColorCoding = "none" | "technician" | "serviceAdvisor";

export function useContainerTimeChartState(selectedDateRange: DateRange) {
  const [firstContainer, setFirstContainer] = useState("");
  const [firstEvent, setFirstEvent] = useState<TimeEvent>("enters");
  const [secondContainer, setSecondContainer] = useState("");
  const [secondEvent, setSecondEvent] = useState<TimeEvent>("exits");
  const [timeUnit, setTimeUnit] = useState<"minutes" | "hours" | "days">(
    "minutes"
  );
  const [showTable, setShowTable] = useState(false);
  const [chartType, setChartType] = useState<"bar" | "scatter">("scatter");
  const [colorCoding, setColorCoding] = useState<ColorCoding>("serviceAdvisor");
  const [currentConfig, setCurrentConfig] = useState<
    Partial<ContainerTimeConfig>
  >({});

  const handleConfigLoad = (config: ContainerTimeConfig) => {
    setFirstContainer(config.firstContainer);
    setFirstEvent(config.firstEvent);
    setSecondContainer(config.secondContainer);
    setSecondEvent(config.secondEvent);
    setTimeUnit(config.timeUnit);
    setColorCoding(config.colorCoding);
    setChartType(config.chartType);
    setCurrentConfig(config);
  };

  useEffect(() => {
    setCurrentConfig({
      firstContainer,
      firstEvent,
      secondContainer,
      secondEvent,
      timeUnit,
      colorCoding,
      chartType,
    });
  }, [
    firstContainer,
    firstEvent,
    secondContainer,
    secondEvent,
    timeUnit,
    colorCoding,
    chartType,
  ]);

  const metricsConfig = useMemo(
    () => ({
      firstContainer,
      firstEvent,
      secondContainer,
      secondEvent,
      timeUnit,
      colorCoding,
      chartType,
      dateRangeStart: selectedDateRange.start,
      dateRangeEnd: selectedDateRange.end,
    }),
    [
      firstContainer,
      firstEvent,
      secondContainer,
      secondEvent,
      timeUnit,
      colorCoding,
      chartType,
      selectedDateRange.start,
      selectedDateRange.end,
    ]
  );

  const hasValidConfiguration = !!firstContainer && !!secondContainer;

  return {
    // state
    firstContainer,
    setFirstContainer,
    firstEvent,
    setFirstEvent,
    secondContainer,
    setSecondContainer,
    secondEvent,
    setSecondEvent,
    timeUnit,
    setTimeUnit,
    chartType,
    setChartType,
    colorCoding,
    setColorCoding,
    showTable,
    setShowTable,
    currentConfig,
    handleConfigLoad,
    // derived
    metricsConfig,
    hasValidConfiguration,
  } as const;
}
